import Tabs from "../tabs";
import { useEffect, useState, useMemo } from 'react';
import { getLeadManagementThunk } from '../../leadMangementThunk';
import { useDispatch, useSelector } from 'react-redux';
import { Box, Tooltip, IconButton } from "@mui/material";
import PersonOffOutlinedIcon from '@mui/icons-material/PersonOffOutlined';
import AssignmentIndOutlinedIcon from '@mui/icons-material/AssignmentIndOutlined';
import HowToRegOutlinedIcon from '@mui/icons-material/HowToRegOutlined';
import GroupAddOutlinedIcon from '@mui/icons-material/GroupAddOutlined';
import PersonRemoveOutlinedIcon from '@mui/icons-material/PersonRemoveOutlined';
import { QR, QrActionButton } from "../../../qrCode/components/qrUi";
import { NameCell, ChipList, PersonChip, MutedText, filterBoxSx } from "../leadUi";
import SnackbarMessage from "../../../../components/snackBar/snackBar";
import DataTableWithCheckbox from "../../../../components/dataTableWithSearch/DataTableWithCheckBox";
import EditIcon from '@mui/icons-material/Edit';
import FilterMultiSelect from "../../../../components/selections/filterMultiSelect";
import AssignAndUnAssignModal from "./assignAndUnAssignModal";
import LeadAddBranchDeptModal from "./leadAddBranchDeptModal";
import { getEmployeeThunk } from "../../../employees/employeeThunk";
// import ContactEditModal from "../../../contact/components/contactEdit";
import ContactProfileTabModal from "../../../contactProfile/contactProfileTabModal";
import { getIndividualUserThunk } from "../../../contact/contactThunk";

export default function LeadManagement() {
    const dispatch = useDispatch();
    const { getLeadManagementLeads, status, error } = useSelector((state) => state.leadManagement);
    const { branchDepartments } = useSelector((state) => state.general);

    const [snackbar, setSnackbar] = useState({
        open: false,
        message: "",
        severity: "success",
    });

    const [state, setState] = useState({
        selectedValue: "1",
        selectedBrachDeptValue: [],
        selectedTagValue: [],
        filteredLeads: [],
        branchDeptOptions: [],
        uniqueTags: [],
        errors: {},
        selectedRowData: null,
        openModal: false,
        addBranchDeptModal: false,
        openContactEditModal: false,
    });

    const { selectedValue, selectedTagValue, selectedBrachDeptValue, filteredLeads, branchDeptOptions, uniqueTags, selectedRowData, openModal, addBranchDeptModal } = state;


    // Fetch leads on mount
    useEffect(() => {
        dispatch(getLeadManagementThunk());
        dispatch(getEmployeeThunk());
    }, [dispatch]);

    // Comma-separated id string -> numeric ids only (DB has values like "NULL", "NaN,85", "")
    const parseIds = (raw) => String(raw ?? "").split(",").map(x => x.trim()).filter(x => x !== "" && !isNaN(Number(x)));

    // Lead has at least one branch department (no branch = On Board view)
    const hasBranch = (item) => parseIds(item.branch_department_id).length > 0;

    // Build unique tag options
    const buildTagOptions = (leads) => {
        const map = new Map();
        leads.forEach(item => {
            const ids = (item.tag_id || "").split(",").map(x => x.trim());
            const names = (item.tag_names || "").split(",").map(x => x.trim());

            ids.forEach((id, idx) => {
                if (!id) return;

                const numericId = Number(id);
                if (isNaN(numericId)) return;

                const label = names[idx] || id;
                if (!map.has(numericId)) map.set(numericId, { value: numericId, label });
            });
        });

        return Array.from(map.values());
    };


    // Build branch options — every branch id present on the leads, so none are silently dropped.
    // Ids missing from the employee's branch list still get an option (fallback label).
    const buildBranchOptions = (leads, branches) => {
        const branchIds = new Set(
            leads.flatMap(item => parseIds(item.branch_department_id))
        );
        const known = branches
            .filter(b => branchIds.has(String(b.id)))
            .map(b => ({ value: b.id, label: b.branch_department_name }));
        const knownIds = new Set(known.map(o => String(o.value)));
        const unknown = [...branchIds]
            .filter(id => !knownIds.has(id))
            .map(id => ({ value: Number(id), label: `Other branch (#${id})` }));
        return [...known, ...unknown];
    };

    // Apply filter and update state (called when radio changes or data changes)
    const applyFilters = (value, leads, branches) => {
        let filtered = [];
        switch (value) {
            case "1":
                filtered = leads.filter(
                    item =>
                        item.assigned_emp_id === null &&
                        hasBranch(item)
                );
                break;
            case "2": filtered = leads.filter(item => item.assigned_emp_id !== null); break;
            case "3": filtered = leads.filter(item => !hasBranch(item)); break;
            default: filtered = leads;
        }

        // const tags = buildTagOptions(filtered);
        const tags = [{ value: 0, label: "unAssign" }, ...buildTagOptions(filtered)];
        const branchOpts = buildBranchOptions(filtered, branches);

        setState(prev => ({
            ...prev,
            selectedValue: value,
            filteredLeads: filtered,
            uniqueTags: tags,
            branchDeptOptions: branchOpts,
            selectedTagValue: tags,
            selectedBrachDeptValue: branchOpts,
        }));
    };

    // Run initial filters when leads arrive
    useEffect(() => {
        if (getLeadManagementLeads.length > 0) {
            applyFilters(selectedValue, getLeadManagementLeads, branchDepartments);
        }
    }, [getLeadManagementLeads, branchDepartments, selectedValue]);

    // Snackbar for errors
    useEffect(() => {
        if (status === "failed") {
            setSnackbar({
                open: true,
                message: error,
                severity: "error",
            });
        }
    }, [status, error]);

    // MultiSelect change handler
    const handleMultiSelectChange = (name, selectedValues) => {
        setState(prev => ({
            ...prev,
            [name]: selectedValues,
            errors: { ...prev.errors, [name]: "" },
        }));
    };

    // Radio button change handler → filter leads + update state
    const handleOptionChange = (event) => {
        const value = event.target.value;
        applyFilters(value, getLeadManagementLeads, branchDepartments);
    };

    // Columns for DataTable
    const columns = [
        { header: "SNO", accessorKey: "sno", enableGlobalFilter: true },
        {
            accessorKey: "name", header: "Name",
            Cell: ({ row }) => (
                <NameCell
                    name={row.original.name}
                    title="View contact profile"
                    onClick={() => { setState(prev => ({ ...prev, openContactEditModal: true, selectedRowData: row?.original })); handleClick(row.original?.wa_contact_id); }}
                />
            )
        },
        // { accessorKey: "email", header: "Email" },
        { accessorKey: "employee_name", header: "Employee Name", Cell: ({ row }) => <PersonChip name={row.original.employee_name} /> },
        { accessorKey: "branch_names", header: "Branch", Cell: ({ row }) => <MutedText value={row.original.branch_names} /> },
        { accessorKey: "department_names", header: "Department", Cell: ({ row }) => <ChipList value={row.original.department_names} color={QR.blue} /> },
        { accessorKey: "tag_names", header: "Tag", Cell: ({ row }) => <ChipList value={row.original.tag_names} /> },
    ];

    // Render filters
    const renderLeadFilter = () => (
        <Box sx={{ display: "flex", flexWrap: "wrap", alignItems: "flex-end", gap: 2 }}>
            {selectedValue !== "3" && (
                <Box sx={filterBoxSx}>
                    <FilterMultiSelect
                        name="selectedBrachDeptValue"
                        label="Branch Dept"
                        options={branchDeptOptions}
                        value={selectedBrachDeptValue || []}
                        onChange={handleMultiSelectChange}
                    />
                </Box>
            )}
            <Box sx={filterBoxSx}>
                <FilterMultiSelect
                    name="selectedTagValue"
                    label="Tag"
                    options={uniqueTags || []}
                    value={selectedTagValue || []}
                    onChange={handleMultiSelectChange}
                />
            </Box>
            {selectedValue !== "3" && (
                <Box sx={{ ml: { sm: "auto" }, width: { xs: "100%", sm: "auto" }, "& .MuiButton-root": { width: { xs: "100%", sm: "auto" } } }}>
                    <QrActionButton
                        variant="solid"
                        color={selectedValue === "1" ? QR.blue : "#fc8006"}
                        gradientTo={selectedValue === "1" ? "#3b82f6" : "#fb923c"}
                        icon={selectedValue === "1" ? <GroupAddOutlinedIcon sx={{ fontSize: 18 }} /> : <PersonRemoveOutlinedIcon sx={{ fontSize: 18 }} />}
                        label={`${selectedValue === "1" ? "Assign Leads" : "Un Assign Leads"}`}
                        onClick={() => setState(prev => ({ ...prev, openModal: true }))}
                    />
                </Box>
            )}
        </Box>
    );

    // Render row actions
    const renderRowActions = ({ row }) => (
        <Box sx={{ display: 'flex', gap: '1rem', justifyContent: "center" }}>
            <Tooltip title="Edit">
                <IconButton onClick={() => setState(prev => ({ ...prev, addBranchDeptModal: true, selectedRowData: row.original }))}>
                    <EditIcon style={{ color: "var(--dark-bg)" }} />
                </IconButton>
            </Tooltip>
        </Box>
    );



    const filterLeads = (leads, key, selectedOptions) => {

        if (!selectedOptions || selectedOptions.length === 0) return [];

        const selectedIds = selectedOptions.map(o => String(o.value));

        const includeNull = selectedIds.includes("0");

        return leads.filter(lead => {
            // Only real numeric ids count; "NULL", "NaN", "" etc. mean "none"
            const ids = parseIds(lead[key]);

            // No ids → include only when "0" (unAssign) is selected
            if (ids.length === 0) return includeNull;

            return ids.some(id => selectedIds.includes(id));
        });
    };


    const filteredLeadDetails = useMemo(() => {

        if (selectedValue === "3") {
            if (selectedTagValue.length === 0) return [];
        } else {
            if (selectedBrachDeptValue.length === 0 || selectedTagValue.length === 0) return [];
        }

        let result = filteredLeads || [];
        
        if (selectedValue !== "3" && selectedBrachDeptValue?.length) {
            result = filterLeads(result, "branch_department_id", selectedBrachDeptValue);
        }

        result = filterLeads(result, "tag_id", selectedTagValue);

        return result;
    }, [filteredLeads, selectedBrachDeptValue, selectedTagValue, selectedValue]);


    const handleClick = (wa_contact_id) => {
        dispatch(getIndividualUserThunk({ wa_contact_id: wa_contact_id }));
    }

    // View cards = the old radio options (Un Assign / Assign / On Board). Counts use the same rules as applyFilters.
    const allLeads = getLeadManagementLeads || [];
    const viewCount = {
        "1": allLeads.filter(item => item.assigned_emp_id === null && hasBranch(item)).length,
        "2": allLeads.filter(item => item.assigned_emp_id !== null).length,
        "3": allLeads.filter(item => !hasBranch(item)).length,
    };
    const viewStats = [
        { value: "1", label: "Un Assign Leads", icon: <PersonOffOutlinedIcon />, color: QR.amber },
        { value: "2", label: "Assign Leads", icon: <AssignmentIndOutlinedIcon />, color: QR.blue },
        { value: "3", label: "On Board Leads", icon: <HowToRegOutlinedIcon />, color: QR.main },
    ].map((v) => ({
        label: v.label,
        value: viewCount[v.value],
        icon: v.icon,
        color: v.color,
        active: selectedValue === v.value,
        onClick: () => handleOptionChange({ target: { value: v.value } }),
    }));

    return (
        <div>
            <Tabs stats={viewStats}>
                <DataTableWithCheckbox
                    status={status}
                    enableRowSelection={false}
                    columns={columns}
                    data={filteredLeadDetails || []}
                    renderFilters={renderLeadFilter()}
                    searchFirst
                    emptyMessage={selectedValue === "1" ? "No unassigned leads." : selectedValue === "2" ? "No assigned leads." : "No on board leads."}
                // renderRowActions={renderRowActions}
                />
            </Tabs>

            <SnackbarMessage
                open={snackbar.open}
                message={snackbar.message}
                severity={snackbar.severity}
                onClose={() => setSnackbar(prev => ({ ...prev, open: false }))}
            />

            <AssignAndUnAssignModal show={openModal} handleClose={() => setState(prev => ({ ...prev, openModal: false }))} stateData={state} selectionWiseLeadDetails={filteredLeadDetails || []} />
            <LeadAddBranchDeptModal show={addBranchDeptModal} handleClose={() => setState(prev => ({ ...prev, addBranchDeptModal: false }))} rowData={selectedRowData} />

            <ContactProfileTabModal
                show={state.openContactEditModal}
                handleClose={() => setState(prev => ({ ...prev, openContactEditModal: false }))}
                rowData={selectedRowData}
                type="leadManagement"
            />
        </div>
    );
}
